package kz.dentalcrm.app.screens.services.services

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeDrawingPadding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.sizeIn
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.FloatingActionButton
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import compose.icons.FeatherIcons
import compose.icons.feathericons.ChevronLeft
import compose.icons.feathericons.Plus
import compose.icons.feathericons.Search
import compose.icons.feathericons.X
import dentalcrm.composeapp.generated.resources.Res
import dentalcrm.composeapp.generated.resources.empty_services
import dentalcrm.composeapp.generated.resources.search
import dentalcrm.composeapp.generated.resources.services
import kz.dentalcrm.app.core.base.Selectable
import kz.dentalcrm.app.core.ui.UiStateScaffold
import kz.dentalcrm.app.core.ui.components.ContentButton
import kz.dentalcrm.app.core.ui.components.UIEmptyListState
import kz.dentalcrm.app.core.ui.components.UIIconBackButton
import kz.dentalcrm.app.core.ui.components.UITextInput
import kz.dentalcrm.app.core.ui.components.lazy_column.PaginatedLazyColumn
import kz.dentalcrm.app.core.ui.components.refresh.PullToRefresh
import kz.dentalcrm.app.core.ui.components.refresh.rememberPullToRefreshState
import kz.dentalcrm.app.data.network.response_model.services.ServiceItem
import kz.dentalcrm.app.data.network.response_model.services_category.ServiceCategory
import kz.dentalcrm.app.navigation.AppNavigator
import kz.dentalcrm.app.screens.protocols.detail.DetailProtocolEvent
import kz.dentalcrm.app.screens.services.add.AddServiceDialog
import kz.dentalcrm.app.screens.services.detail.EditServiceDialog
import kz.dentalcrm.app.screens.services.services.mvi.ServicesState
import kz.dentalcrm.app.screens.services.services.mvi.ServicesViewEvent
import kz.dentalcrm.app.screens.ui.ContentHeader
import kz.dentalcrm.app.screens.ui.Icons.chevronRight
import kz.dentalcrm.app.theme.DefaultTheme
import kz.dentalcrm.app.theme.shimmer.ShimmerBounds
import kz.dentalcrm.app.theme.shimmer.rememberShimmer
import kz.dentalcrm.app.theme.shimmer.shimmer
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.koinInject
import org.koin.compose.viewmodel.koinViewModel

@Composable
fun ServicesScreen() {
    val navigator: AppNavigator = koinInject()
    val viewModel: ServicesViewModel = koinViewModel()
    Box(
        modifier = Modifier.fillMaxSize(),
        contentAlignment = Alignment.Center
    ) {
        val state by viewModel.viewState.collectAsState()

        UiStateScaffold(
            modifier = Modifier.fillMaxSize(),
            content = {
                ServicesContent(
                    state = state,
                    onBack = { navigator.navigateUp() },
                    event = { event -> viewModel.handleEvents(event) }
                )
            }
        )
    }
}

@Composable
private fun ServicesContent(
    state: ServicesState,
    event: (ServicesViewEvent) -> Unit,
    onBack: () -> Unit
) {
    val isLoading = state.screenState is ServicesState.ScreenState.Loading
    val shimmer = rememberShimmer(ShimmerBounds.Window)
    val listState = rememberLazyListState()
    var isRefreshing by remember { mutableStateOf(false) }
    val pullToRefreshState = rememberPullToRefreshState(isRefreshing = isRefreshing)
    LaunchedEffect(isLoading) { if (!isLoading) isRefreshing = false }

    var showAddServiceDialog by remember { mutableStateOf(false) }
    var showEditServiceDialog by remember { mutableStateOf<ServiceItem?>(null) }

    if (showAddServiceDialog) {
        AddServiceDialog(
            onAdd = { event(ServicesViewEvent.Reload) },
            onDismiss = { showAddServiceDialog = false }
        )
    }

    showEditServiceDialog?.let { service ->
        EditServiceDialog(
            item = service,
            onEdit = { event(ServicesViewEvent.Reload) },
            onDelete = { id -> event(ServicesViewEvent.DeleteService(id)) },
            onDismiss = { showEditServiceDialog = null }
        )
    }

    Box(
        modifier = Modifier.fillMaxSize()
            .safeDrawingPadding()
    ) {
        Column(modifier = Modifier.sizeIn()) {
            Spacer(modifier = Modifier.size(8.dp))
            ContentHeader(
                modifier = Modifier.fillMaxWidth().padding(horizontal = 12.dp),
                text = stringResource(Res.string.services),
                leadingContent = {
                    UIIconBackButton(
                        onClick = {
                            onBack()
                        }
                    )
                }
            )
            UITextInput(
                modifier = Modifier.fillMaxWidth().padding(horizontal = 12.dp),
                label = stringResource(Res.string.search),
                borderWidth = 1.dp,
                leadingIcon = FeatherIcons.Search,
                trailingIcon = FeatherIcons.X
            ) { search ->
                event(ServicesViewEvent.SearchService(search))
            }
            Spacer(modifier = Modifier.height(8.dp))
            LazyRow {
                item { Spacer(modifier = Modifier.width(12.dp)) }
                items(items = state.serviceCategories, key = { it.data.id }) {
                    ServiceCategoryItem(item = it) {
                        if (it.selected) {
                            event(ServicesViewEvent.ClearSelectedCategories)
                        } else {
                            event(ServicesViewEvent.ServiceCategorySelected(category = it.data))
                        }
                    }
                    Spacer(modifier = Modifier.width(8.dp))
                }
            }
            Spacer(modifier = Modifier.size(8.dp))

            PullToRefresh(
                state = pullToRefreshState,
                onRefresh = { isRefreshing = true; event(ServicesViewEvent.Reload) },
                modifier = Modifier.fillMaxSize()
            ) {
                if (isLoading && state.services.isEmpty()) {
                    Column(modifier = Modifier.widthIn().padding(horizontal = 12.dp)) {
                        Spacer(modifier = Modifier.height(16.dp))
                        repeat(8) { ServiceItemSkeleton(shimmer) }
                    }
                } else if (!isLoading && state.services.isEmpty()) {
                    UIEmptyListState(text = stringResource(Res.string.empty_services))
                } else {
                    val listModifier = if (isLoading)
                        Modifier.widthIn().padding(horizontal = 12.dp).shimmer(shimmer)
                    else
                        Modifier.widthIn().padding(horizontal = 12.dp)
                    PaginatedLazyColumn(
                        modifier = listModifier,
                        loadMoreItems = { event(ServicesViewEvent.LoadMore) },
                        listState = listState,
                        buffer = 5,
                        isLoading = isLoading,
                        paginatedTextError = (state.screenState as? ServicesState.ScreenState.Error)?.text,
                        retryOnPaginated = { event(ServicesViewEvent.LoadMore) }
                    ) {
                        item { Spacer(modifier = Modifier.height(16.dp)) }
                        items(state.services, key = { it.id }) { item ->
                            ServiceItem(item = item) { showEditServiceDialog = item }
                        }
                    }
                }
            }
        }
        FloatingActionButton(
            modifier = Modifier
                .navigationBarsPadding()
                .padding(bottom = 32.dp, start = 32.dp, end = 16.dp, top = 16.dp)
                .align(Alignment.BottomEnd),
            shape = RoundedCornerShape(50),
            containerColor = DefaultTheme.colors.primary,
            contentColor = DefaultTheme.colors.onPrimary,
            onClick = { showAddServiceDialog = true }
        ) {
            Icon(imageVector = FeatherIcons.Plus, contentDescription = null)
        }
    }
}

@Composable
private fun ServiceItemSkeleton(shimmer: kz.dentalcrm.app.theme.shimmer.Shimmer) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .background(
                color = DefaultTheme.colors.primarySubdued,
                shape = RoundedCornerShape(16.dp)
            ),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Spacer(modifier = Modifier.width(12.dp))
        Column(modifier = Modifier.weight(1f)) {
            Spacer(modifier = Modifier.height(12.dp))
            Box(
                modifier = Modifier
                    .fillMaxWidth(0.55f)
                    .height(14.dp)
                    .shimmer(shimmer)
                    .background(
                        color = DefaultTheme.colors.onSurface.copy(alpha = 0.12f),
                        shape = RoundedCornerShape(4.dp)
                    )
            )
            Spacer(modifier = Modifier.height(4.dp))
            Box(
                modifier = Modifier
                    .fillMaxWidth(0.8f)
                    .height(12.dp)
                    .shimmer(shimmer)
                    .background(
                        color = DefaultTheme.colors.onSurface.copy(alpha = 0.12f),
                        shape = RoundedCornerShape(4.dp)
                    )
            )
            Spacer(modifier = Modifier.height(6.dp))
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(1.dp)
                    .background(color = DefaultTheme.colors.onSurfaceVariant.copy(alpha = 0.2f))
            )
            Spacer(modifier = Modifier.height(6.dp))
            Box(
                modifier = Modifier
                    .fillMaxWidth(0.35f)
                    .height(12.dp)
                    .shimmer(shimmer)
                    .background(
                        color = DefaultTheme.colors.onSurface.copy(alpha = 0.12f),
                        shape = RoundedCornerShape(4.dp)
                    )
            )
            Spacer(modifier = Modifier.height(12.dp))
        }
        Spacer(modifier = Modifier.width(32.dp))
    }
    Spacer(Modifier.height(12.dp))
}

@Composable
fun ServiceItem(
    item: ServiceItem,
    onClick: () -> Unit,
) {
    ContentButton(
        modifier = Modifier,
        shape = RoundedCornerShape(16.dp),
        onClick = onClick
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .heightIn()
                .background(
                    color = DefaultTheme.colors.primarySubdued,
                    shape = RoundedCornerShape(16.dp)
                ),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Spacer(modifier = Modifier.width(12.dp))
            Column(modifier = Modifier.weight(1f)) {
                Spacer(modifier = Modifier.height(12.dp))
                Text(
                    text = item.name ?: "",
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                    color = DefaultTheme.colors.onSurface,
                    style = DefaultTheme.typography.bodyMediumMedium,
                )
                Spacer(modifier = Modifier.height(2.dp))
                Text(
                    text = item.description ?: "",
                    overflow = TextOverflow.Ellipsis,
                    maxLines = 3,
                    color = DefaultTheme.colors.onSurfaceVariant,
                    style = DefaultTheme.typography.bodySmallRegular,
                )
                Spacer(modifier = Modifier.height(4.dp))
                HorizontalDivider(
                    modifier = Modifier.fillMaxWidth(),
                    thickness = 0.5.dp,
                    color = DefaultTheme.colors.onSurfaceVariant.copy(alpha = 0.8f)
                )
                Spacer(modifier = Modifier.height(4.dp))
                Text(
                    text = item.category?.name ?: "",
                    overflow = TextOverflow.Ellipsis,
                    maxLines = 2,
                    color = DefaultTheme.colors.onSurfaceVariant,
                    style = DefaultTheme.typography.bodySmallRegular,
                )
                Spacer(modifier = Modifier.height(12.dp))
            }
            Spacer(modifier = Modifier.width(8.dp))
            Icon(
                imageVector = chevronRight,
                tint = DefaultTheme.colors.onSurface,
                contentDescription = null
            )
            Spacer(modifier = Modifier.width(8.dp))
        }
    }
    Spacer(Modifier.height(12.dp))
}

@Composable
fun ServiceCategoryItem(
    item: Selectable<ServiceCategory>,
    onClick: () -> Unit,
) {
    ContentButton(
        modifier = Modifier,
        shape = RoundedCornerShape(16.dp),
        onClick = onClick
    ) {
        Row(
            modifier = Modifier
                .widthIn()
                .heightIn(min = 32.dp)
                .background(
                    color = if (item.selected) DefaultTheme.colors.primary else DefaultTheme.colors.primarySubdued,
                    shape = RoundedCornerShape(16.dp)
                ),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Spacer(modifier = Modifier.width(12.dp))
            Text(
                text = item.data.name ?: "",
                overflow = TextOverflow.Ellipsis,
                maxLines = 1,
                color = if (item.selected) DefaultTheme.colors.onPrimary else DefaultTheme.colors.onSurface,
                style = DefaultTheme.typography.bodySmallRegular,
            )
            Spacer(modifier = Modifier.width(12.dp))
        }
    }
    Spacer(Modifier.height(12.dp))
}
